// 範囲指定（マーキー）選択ハンドラ。
//
// 振る舞い:
// - 右ボタン（副ボタン）でのドラッグで範囲指定枠を表示し、離した時点で枠に重なる
//   オブジェクトをまとめて選択する。
// - ペンストロークだけは外接矩形ではなく、ストローク近傍（線の太さ + 画面上のマージン）と
//   枠が交差した場合に選択する。外接矩形で判定すると、ストロークが囲む空白だけを囲った
//   場合にも選択されてしまうため。判定の詳細は lib/marquee.ts と lib/pen-stroke-hit.ts を参照。
// - しきい値（MARQUEE_THRESHOLD_PX）を超えて初めて範囲選択を「アクティブ化」する。
//   それ未満の移動は「ただの右クリック」とみなし、選択も枠表示もせず、右クリック
//   メニュー（コンテキストメニュー）を従来どおり表示させる。
// - Ctrl / Meta / Shift を押しながら離すと既存の選択へ追加（和集合）、押していない
//   ときは選択を置換する。
// - 範囲選択ドラッグが成立した直後の contextmenu イベントは抑止し、枠選択操作が
//   ブラウザ標準メニューやオブジェクトの右クリックメニューに化けないようにする。
//
// 左ボタンのパン / オブジェクトドラッグ / ペン描画 / 配置とは排他で、それらは
// use-pointer-mode / use-object-drag 側が主ボタン（button === 0）のみで処理する。
//
// 入力所有権:
// - 範囲指定は選択ツール（tool === "idle"）専用とする。反転 ON では選択ボタンがペンと同じ
//   左ボタンになるため、ツールを見ずにボタンだけで候補を作ると 1 つのポインタをペンと
//   範囲指定が二重に所有し、pointer capture の解放と終了処理が競合する（Issue #13）。
// - 開始時に所有したポインタは、その後にツール設定が変わっても同じ所有者が
//   pointerup / pointercancel まで処理する。新しいツール設定は次の pointerdown から効く。
// - 編集中の本文・入力欄（contentEditable / input / textarea / select とその子孫）から始まった
//   ポインタは、ボタンや反転設定によらず範囲指定の候補にしない。文字選択・キャレット操作を優先する
//   （Issue #160。判定は editing-target.ts の isEditablePointerTarget を共有する）。
//
// 既知の制約（主たる対象環境は Windows + マウスで、そこでは下記いずれも問題ない）:
// - contextmenu の抑止は「contextmenu が pointerup 後に発火する」前提（Windows / 多くの
//   ブラウザ）に依存する。WebKit/Safari や Linux の Chromium など contextmenu を pointerdown
//   起点で発火する実装では、右ドラッグ開始時にメニューが出てしまう（空白部なら canvas-stage が
//   preventDefault して開くカスタムメニュー、オブジェクト上なら canvas-objects-layer が開く
//   メニュー、どちらも受けない場所ならブラウザ標準メニュー）。pointerdown 時点ではクリックか
//   ドラッグか判別できないため、ここで一律抑止すると通常の右クリックメニューを壊す。
//   トレードオフとして「通常の右クリックメニューを優先」する設計とする。
//   なお空白部のカスタムメニュー（empty-area-context-menu.tsx）は、押下したままの contextmenu で
//   開いた場合だけボタンを離すまで pointer-events: none で素通しにするため、ポインタの真下に
//   メニューが現れても、ここへ届く pointermove / pointerup は奪われない。
// - secondary click 判定は button === 2 を用いる。macOS の Control+クリックは WebKit が
//   button === 0 を返す既知差分があり範囲選択の起点にはならない（button 0 + Ctrl は複数
//   選択トグルに割り当て済みのため流用できない）。macOS では実右ボタン / トラックパッドの
//   副ボタンクリック（button === 2）で範囲選択を行う。

import { useEffect, useRef, type RefObject } from "react";
import { useCanvasStore } from "../store/canvas-store";
import { useCanvasDocStore } from "../store/canvas-doc-store";
import { screenToStage, ZOOM_MIN } from "../../../lib/canvas-coordinates";
import { normalizeMarqueeRect, objectIdsInMarquee } from "../../../lib/marquee";
import { PEN_HIT_MARGIN_SCREEN_PX } from "../../../lib/pen-stroke-hit";
import { isEditablePointerTarget } from "./editing-target";

// ドラッグがこの距離（クライアント px）を超えたら範囲選択として扱う。
const MARQUEE_THRESHOLD_PX = 4;
// マウスの主ボタン（左クリック）と副ボタン（右クリック）。
const PRIMARY_BUTTON = 0;
const SECONDARY_BUTTON = 2;

interface MarqueeDragState {
  readonly pointerId: number;
  readonly startClientX: number;
  readonly startClientY: number;
  readonly startStageX: number;
  readonly startStageY: number;
  /** しきい値を超えて範囲選択がアクティブ化したか。 */
  active: boolean;
  /** ドラッグ成立後に直後の contextmenu を抑止するか（右ボタン選択時のみ）。 */
  readonly suppressesContextMenu: boolean;
  /**
   * このフック自身が pointer capture を取得したか。
   * 取得していないポインタの capture（他の入力所有者のもの）を解放しないための目印。
   */
  captured: boolean;
}

export function useMarqueeSelection(
  containerRef: RefObject<HTMLElement | null>,
  options?: { readonly readOnly?: boolean | undefined }
): void {
  const readOnly = options?.readOnly ?? false;
  const dragStateRef = useRef<MarqueeDragState | null>(null);
  // 範囲選択ドラッグ直後の contextmenu を 1 回だけ抑止するためのフラグ。
  const suppressContextMenuRef = useRef(false);

  useEffect(() => {
    // 参照専用（埋め込み）では範囲指定選択を行わない。
    if (readOnly) return undefined;
    const container = containerRef.current;
    if (!container) return undefined;

    // 進行中の範囲指定ジェスチャの状態を破棄する共通後始末。
    // 自分が取得した pointer capture だけを解放し、他の入力所有者（ペン等）の capture へは触れない。
    const endMarqueeGesture = (dragState: MarqueeDragState): void => {
      dragStateRef.current = null;
      if (dragState.captured && container.hasPointerCapture(dragState.pointerId)) {
        container.releasePointerCapture(dragState.pointerId);
      }
    };

    const onPointerDown = (event: PointerEvent): void => {
      // 既に別ポインタの範囲指定を処理中なら、所有権を移さず新しい候補も作らない。
      // 開始済みのジェスチャは、そのポインタの pointerup / pointercancel で完了させる。
      // 抑止フラグも巻き込んで落とさない（進行中の右ドラッグを壊さないマルチポインタ対策）。
      if (dragStateRef.current) return;
      // 新しいジェスチャの開始時にだけ、前のジェスチャで立てた抑止フラグをリセットする。
      // （右ボタンドラッグ後に普通の右クリックをしたとき、メニューが出ないままになる退行を防ぐ。）
      suppressContextMenuRef.current = false;
      // 範囲指定は選択ツール専用。ペン・配置・パンなど他ツールのポインタは取得しない。
      // ツール状態は getState() で都度読み、effect のリスナ再登録（依存配列）は変えない。
      if (useCanvasStore.getState().tool !== "idle") return;
      // 選択操作の左右反転: 反転 OFF は右（副）ボタン、反転 ON は左（主）ボタンで範囲選択する。
      const selectButton = useCanvasStore.getState().invertSelectPan
        ? PRIMARY_BUTTON
        : SECONDARY_BUTTON;
      if (event.button !== selectButton) return;
      // 編集中の本文・入力欄（contentEditable / input / textarea / select とその子孫）から始まった
      // ポインタは文字選択・キャレット操作の入力なので、範囲指定の候補にしない（Issue #160）。
      // 反転 ON では左ボタンが範囲指定を担うため、ここで除外しないと文字のドラッグ選択がしきい値を
      // 超えた時点で範囲指定へ昇格し、container が pointer capture して範囲指定の枠を出し、離した時点で
      // 図形の選択まで置き換えてしまう（捕捉されている間は、編集面の外へ出たときの文字選択の保持
      // 〈use-bounded-text-selection〉も効かなくなる）。
      if (isEditablePointerTarget(event)) return;
      const view = useCanvasStore.getState().view;
      const start = screenToStage({ x: event.clientX, y: event.clientY }, view);
      dragStateRef.current = {
        pointerId: event.pointerId,
        startClientX: event.clientX,
        startClientY: event.clientY,
        startStageX: start.x,
        startStageY: start.y,
        active: false,
        // 右ボタンでの範囲選択のときだけ、ドラッグ成立後の contextmenu を抑止する
        // （左ボタン＝反転 ON では右クリックのメニューが発生しないため抑止不要）。
        suppressesContextMenu: selectButton === SECONDARY_BUTTON,
        captured: false,
      };
      // ここでは pointer capture しない。capture するとブラウザによっては後続の
      // contextmenu の target が capture 先（container）へ寄り、しきい値未満の通常の
      // 右クリックでもオブジェクトの右クリックメニュー（onContextMenuCapture）が
      // 開かなくなる。capture は実際にドラッグが成立した時点（onPointerMove）で行う。
    };

    const onPointerMove = (event: PointerEvent): void => {
      const dragState = dragStateRef.current;
      if (!dragState || event.pointerId !== dragState.pointerId) return;
      const distance = Math.hypot(
        event.clientX - dragState.startClientX,
        event.clientY - dragState.startClientY
      );
      if (!dragState.active) {
        // しきい値未満の手ブレでは枠を出さない（ただの右クリックを保護する）。
        if (distance <= MARQUEE_THRESHOLD_PX) return;
        dragState.active = true;
        // ドラッグが成立した時点で、直後の contextmenu を抑止対象にする（右ボタン選択時のみ）。
        if (dragState.suppressesContextMenu) suppressContextMenuRef.current = true;
        // この時点で初めて capture し、ポインタが container 外や別要素へ移っても
        // move を取りこぼさないようにする（通常の右クリックでは capture しない）。
        if (!container.hasPointerCapture(dragState.pointerId)) {
          container.setPointerCapture(dragState.pointerId);
          dragState.captured = true;
        }
      }
      const view = useCanvasStore.getState().view;
      const current = screenToStage({ x: event.clientX, y: event.clientY }, view);
      useCanvasStore
        .getState()
        .setMarqueeRect(
          normalizeMarqueeRect(dragState.startStageX, dragState.startStageY, current.x, current.y)
        );
    };

    const onPointerUp = (event: PointerEvent): void => {
      const dragState = dragStateRef.current;
      if (!dragState || event.pointerId !== dragState.pointerId) return;
      endMarqueeGesture(dragState);
      // アクティブ化していない＝ただの右クリック。枠選択せずメニューはそのまま出させる。
      if (!dragState.active) return;

      const view = useCanvasStore.getState().view;
      const current = screenToStage({ x: event.clientX, y: event.clientY }, view);
      const rect = normalizeMarqueeRect(
        dragState.startStageX,
        dragState.startStageY,
        current.x,
        current.y
      );
      useCanvasStore.getState().setMarqueeRect(null);
      const objects = useCanvasDocStore.getState().objects;
      // コメントスレッドは専用オーバーレイで扱い、範囲選択（および選択経由の削除 / コピー等）の
      // 対象にしない。
      // ペンストロークだけは外接矩形ではなくストローク近傍（線の太さ + マージン）で判定する。
      // マージンは画面上の px で定義されているため、stage 座標へ換算して渡す。
      // zoom は ZOOM_MIN でクランプし、0 除算と Infinity の混入を防ぐ。
      const hitIds = objectIdsInMarquee(objects, rect, {
        penMarginStagePx: PEN_HIT_MARGIN_SCREEN_PX / Math.max(view.zoom, ZOOM_MIN),
      }).filter((id) => objects[id]?.type !== "comment-thread");
      // 修飾キー併用時は既存選択へ追加（和集合）、それ以外は置換する。
      const additive = event.ctrlKey || event.metaKey || event.shiftKey;
      if (additive) {
        const merged = new Set(useCanvasStore.getState().selectedObjectIds);
        for (const id of hitIds) merged.add(id);
        useCanvasStore.getState().setSelectedObjectIds(Array.from(merged));
      } else {
        useCanvasStore.getState().setSelectedObjectIds(hitIds);
      }
    };

    // 選択を確定せずにジェスチャを破棄する共通処理。
    // 中断されたジェスチャには後続の contextmenu が無いため、抑止フラグを残さない。
    // 残すと、次にキーボード（Shift+F10 / メニューキー）で開く正当なコンテキスト
    // メニューを 1 回だけ誤って抑止してしまう。
    const discardMarqueeGesture = (dragState: MarqueeDragState): void => {
      endMarqueeGesture(dragState);
      suppressContextMenuRef.current = false;
      useCanvasStore.getState().setMarqueeRect(null);
    };

    const onPointerCancel = (event: PointerEvent): void => {
      const dragState = dragStateRef.current;
      if (!dragState || event.pointerId !== dragState.pointerId) return;
      discardMarqueeGesture(dragState);
    };

    // pointerup を伴わない pointer capture 喪失（要素の差し替え・OS 都合の中断など）の受け皿。
    // アクティブ化後は capture 済みのため container の pointerup で片付く前提だが、capture を
    // 失うとその pointerup が届かず、進行中の候補と枠が残って以後の範囲指定を開始できなくなる
    // （新しい pointerdown が「処理中」と判定されるため）。選択は確定せず破棄する。
    // 通常の pointerup は先に候補を片付けてから capture を解放するので、ここでは二重処理しない。
    const onLostPointerCapture = (event: PointerEvent): void => {
      const dragState = dragStateRef.current;
      if (!dragState || event.pointerId !== dragState.pointerId) return;
      discardMarqueeGesture(dragState);
    };

    // しきい値到達前は pointer capture を取らないため、container の外でボタンが離されると
    // container の pointerup / pointercancel を取りこぼし、候補（dragStateRef）が残り得る。
    // 候補が残ると、以降の pointerdown が「処理中」と判定されて範囲指定を開始できなくなるため、
    // window でも解放を監視し、未アクティブの候補だけを破棄する（アクティブ後は capture 済みで
    // container が確実に受け取るので、ここでは触らない）。
    const clearPendingMarquee = (event: PointerEvent): void => {
      const dragState = dragStateRef.current;
      if (!dragState || dragState.active) return;
      if (event.pointerId !== dragState.pointerId) return;
      dragStateRef.current = null;
    };

    // 範囲選択ドラッグ直後の contextmenu を抑止する。window の capture フェーズで処理し、
    // ブラウザ標準メニューと、React がルートで拾うオブジェクトの右クリックメニューの
    // 両方より先に preventDefault / stopPropagation する。
    const onContextMenu = (event: MouseEvent): void => {
      if (!suppressContextMenuRef.current) return;
      event.preventDefault();
      event.stopPropagation();
      suppressContextMenuRef.current = false;
    };

    container.addEventListener("pointerdown", onPointerDown);
    container.addEventListener("pointermove", onPointerMove);
    container.addEventListener("pointerup", onPointerUp);
    container.addEventListener("pointercancel", onPointerCancel);
    container.addEventListener("lostpointercapture", onLostPointerCapture);
    window.addEventListener("pointerup", clearPendingMarquee);
    window.addEventListener("pointercancel", clearPendingMarquee);
    window.addEventListener("contextmenu", onContextMenu, { capture: true });
    return () => {
      container.removeEventListener("pointerdown", onPointerDown);
      container.removeEventListener("pointermove", onPointerMove);
      container.removeEventListener("pointerup", onPointerUp);
      container.removeEventListener("pointercancel", onPointerCancel);
      container.removeEventListener("lostpointercapture", onLostPointerCapture);
      window.removeEventListener("pointerup", clearPendingMarquee);
      window.removeEventListener("pointercancel", clearPendingMarquee);
      window.removeEventListener("contextmenu", onContextMenu, { capture: true });
      // アンマウントや参照専用への切替でも、進行中ジェスチャの状態を残さない
      // （選択は確定せず、枠と抑止フラグだけを片付ける）。
      const dragState = dragStateRef.current;
      if (dragState) discardMarqueeGesture(dragState);
      suppressContextMenuRef.current = false;
    };
  }, [containerRef, readOnly]);
}
